import { FilterCheckbox } from 'components/Blocks/Product/Filter/FilterCheckbox/FilterCheckbox';
import { useCurrentFilterQuery } from 'helpers/queryParams/useCurrentFilterQuery';
import { useUpdateFilterQuery } from 'helpers/queryParams/useUpdateFilterQuery';
import { useFilterShowLess } from 'hooks/filter/useFilterShowLess';
import { useTypedTranslationFunction } from 'hooks/typescript/UseTypedTranslationFunction';
import { ArrowIcon } from 'public/svg/IconsSvg';
import { useState } from 'react';
import { useSessionStore } from 'store/useSessionStore';
import { twJoin } from 'tailwind-merge';

type FilterFieldType = 'flags' | 'brands';

export type MappedFilterOption = { name: string; uuid: string; count?: number };

type FilterGroupProps = {
    title: string;
    filterField: FilterFieldType;
    options: MappedFilterOption[];
    defaultNumberOfShownItems: number;
};

export const filterGroupTwClass =
    'border-b px-7 border-borderLight ease-defaultTransition duration-200 last:border-b-0';
export const filterGroupTitleTwClass =
    'block relative p-5 pl-0 text-black text-body font-medium cursor-pointer hover:text-green';
export const filterGroupShowAllButtonTwClass =
    'w-fit p-0 text-small border-0 bg-none outline-0 text-black cursor-pointer underline hover:no-underline hover:bg-none hover:text-primary';
export const getFilterGroupContentTwClass = (isOpen: boolean): string =>
    twJoin('flex-wrap flex-col mb-2', isOpen ? 'flex' : 'hidden');
export const getFilterGroupArrowTwClass = (isOpen: boolean): string =>
    twJoin(
        'absolute right-0 top-1/2 w-[14px] text-grayDarker select-none -translate-y-1/2 rotate-0 ease-defaultTransition duration-200',
        isOpen && 'rotate-180',
    );
export const getFilterGroupContentItemTwClass = (isDisabled: boolean, isActive: boolean): string =>
    twJoin('mb-2', isDisabled && !isActive && 'opacity-30 pointer-events-none');

export const FilterGroup: FC<FilterGroupProps> = ({ title, filterField, defaultNumberOfShownItems, options }) => {
    const t = useTypedTranslationFunction();
    const [isGroupOpen, setIsGroupOpen] = useState(true);
    const { updateFilterFlagsQuery, updateFilterBrandsQuery } = useUpdateFilterQuery();
    const filterQuery = useCurrentFilterQuery();
    const defaultSelectedFlags = useSessionStore((s) => s.defaultProductFiltersMap.flags);

    const selectedItems = filterQuery && filterQuery[filterField];

    const { defaultOptions, isShowLessMoreShown, isWithAllItemsShown, setAreAllItemsShown } = useFilterShowLess(
        options,
        defaultNumberOfShownItems,
        selectedItems,
    );

    const handleCheck = (uuid: string) => {
        switch (filterField) {
            case 'brands':
                updateFilterBrandsQuery(uuid);
                break;
            case 'flags':
                updateFilterFlagsQuery(uuid);
                break;
        }
    };

    return (
        <div className={filterGroupTwClass}>
            <div className={filterGroupTitleTwClass} onClick={() => setIsGroupOpen((prev) => !prev)}>
                {title}
                <ArrowIcon className={getFilterGroupArrowTwClass(isGroupOpen)} />
            </div>
            {isGroupOpen && defaultOptions && (
                <div className={getFilterGroupContentTwClass(isGroupOpen)}>
                    {defaultOptions.map((option, index) => {
                        const isFlagAndSelectedByDefault =
                            filterField === 'flags' && defaultSelectedFlags.has(option.uuid);
                        const isChecked = !!selectedItems?.includes(option.uuid) || isFlagAndSelectedByDefault;
                        const isDisabled = option.count === 0 && !isChecked;

                        return (
                            <div className={getFilterGroupContentItemTwClass(isDisabled, isChecked)} key={option.uuid}>
                                <FilterCheckbox
                                    name={`${filterField}.${index}.checked`}
                                    id={`${filterField}.${index}.checked`}
                                    label={option.name}
                                    onChange={() => handleCheck(option.uuid)}
                                    count={option.count}
                                    value={isChecked}
                                />
                            </div>
                        );
                    })}
                    {isShowLessMoreShown && (
                        <button
                            tabIndex={0}
                            className={filterGroupShowAllButtonTwClass}
                            onClick={() => setAreAllItemsShown((prev) => !prev)}
                        >
                            {isWithAllItemsShown ? t('show less') : t('show more')}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};
